import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../common/Button';
import Card, { CardBody, CardFooter, CardHeader } from '../common/Card';
import VoiceInput from '../common/VoiceInput';
import AmbientBackground from '../common/AmbientBackground';
import RadialMotif from '../common/RadialMotif';
import { useReadingsStore } from '../../store/readingsStore';
import { generateInterpretation } from '../../services/readingsService';
import { useAuthStore } from '../../store/authStore';

const loadingMessages = [
  "Your reading is being enhanced with deeper insights. Wisdom takes time!",
  "Weaving threads of insight into your reading...",
  "Channeling the cards' deeper meanings...",
  "Uncovering layers of wisdom in your spread...",
  "Drawing forth the cards' hidden messages...",
  "Illuminating the path ahead...",
  "Revealing the cards' sacred guidance...",
  "Discovering deeper connections in your reading...",
  "Exploring the mystical meanings within...",
  "Bringing clarity to your journey..."
];

const ongoingRequests = new Map<string, Promise<any>>();

const InterpretationView: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { currentReading, updateCurrentReading, saveCurrentReading, storePendingReading } = useReadingsStore();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [saveAttempts, setSaveAttempts] = useState(0);
  const maxSaveAttempts = 3;

  useEffect(() => {
    if (!currentReading) {
      navigate('/new-reading');
      return;
    }

    const fetchInterpretation = async () => {
      if (!currentReading.spreadType || !currentReading.cards || !currentReading.context) {
        setError('Reading information is incomplete. Please ensure you have selected cards and provided context.');
        setIsLoading(false);
        return;
      }

      try {
        const requestKey = `${currentReading.spreadType}-${currentReading.context}-${JSON.stringify(currentReading.cards)}`;

        let interpretationPromise = ongoingRequests.get(requestKey);

        if (!interpretationPromise) {
          interpretationPromise = generateInterpretation(
            currentReading.spreadType,
            currentReading.cards,
            currentReading.context,
            currentReading.userInterpretation || '',
            currentReading.readingTitle
          );

          ongoingRequests.set(requestKey, interpretationPromise);

          interpretationPromise.finally(() => {
            ongoingRequests.delete(requestKey);
          });
        }

        // Start progress animation
        const progressInterval = setInterval(() => {
          setLoadingProgress(prev => {
            if (prev >= 90) return prev; // Cap at 90% until complete
            return prev + 1;
          });
        }, 300);

        const result = await interpretationPromise;

        // Complete progress
        clearInterval(progressInterval);
        setLoadingProgress(100);

        if (!result) {
          throw new Error('Failed to generate interpretation - no data received');
        }

        updateCurrentReading({
          aiInterpretation: result.interpretation,
          themes: result.themes,
          advice: result.advice,
          readingTitle: result.readingTitle
        });

        setIsLoading(false);
      } catch (err) {
        console.error('Error generating interpretation:', err);
        setError(err instanceof Error ? err.message : 'An unknown error occurred while generating your reading');
        setIsLoading(false);
      }
    };

    if (!currentReading.aiInterpretation) {
      const messageInterval = setInterval(() => {
        setLoadingMessageIndex(prev => (prev + 1) % loadingMessages.length);
      }, 3000);

      fetchInterpretation();

      return () => clearInterval(messageInterval);
    } else {
      setIsLoading(false);
    }
  }, [currentReading, navigate, updateCurrentReading]);

  const handleSave = async () => {
    if (!currentReading || isSaving) return;

    setIsSaving(true);
    setError(null);

    try {
      // Verify auth state inside try/catch
      const { user, session } = useAuthStore.getState();
      if (!user) {
        throw new Error('Authentication required');
      }

      // Check session validity
      if (session?.expires_at && session.expires_at <= Math.floor(Date.now() / 1000)) {
        // Session expired, try to refresh
        const { data: { session: refreshedSession }, error: refreshError } =
          await supabase.auth.refreshSession();

        if (refreshError || !refreshedSession) {
          throw new Error('Session expired. Please log in again.');
        }
      }

      // Update reading with notes
      updateCurrentReading({ notes });

      // Implement retry logic
      let savedReading = null;
      let lastError = null;
      const maxRetries = 3;
      const baseDelay = 1000; // 1 second

      for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
          setSaveAttempts(attempt + 1);
          savedReading = await saveCurrentReading();
          if (savedReading) break;
        } catch (err) {
          lastError = err;
          if (attempt < maxRetries - 1) {
            // Exponential backoff
            await new Promise(resolve =>
              setTimeout(resolve, baseDelay * Math.pow(2, attempt))
            );
          }
        }
      }

      if (!savedReading) {
        throw lastError || new Error('Failed to save reading after multiple attempts');
      }

      // For test readings or virtual readings, skip photo upload
      if (currentReading.is_test || currentReading.is_virtual) {
        navigate('/next-steps');
      } else {
        navigate(`/upload-photo/${savedReading.id}`);
      }
    } catch (err) {
      console.error('Error saving reading:', err);
      setError(err instanceof Error ? err.message : 'Failed to save reading. Please try again.');
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <AmbientBackground mood="moon" backgroundImage="/bg-interpretation.png">
        <AnimatePresence mode="wait">
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.3 } }}
            className="min-h-[60vh] flex flex-col items-center justify-center text-center"
          >
            <div className="relative w-[120px] h-[120px] mb-8 flex items-center justify-center">
              <RadialMotif
                size={80}
                className="text-moon dark:text-dusk"
              />
              <div className="absolute inset-0 radial-pulse text-moon/40 dark:text-dusk/40" />
            </div>
            <div className="w-full max-w-md mb-8">
              <div className="h-2 bg-moon/20 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-moon to-dusk"
                  initial={{ width: "0%" }}
                  animate={{ width: `${loadingProgress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </div>
            <motion.p
              key={loadingMessageIndex}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="font-display italic text-2xl text-moon dark:text-dusk mb-4"
            >
              {loadingMessages[loadingMessageIndex]}
            </motion.p>
          </motion.div>
        </AnimatePresence>
      </AmbientBackground>
    );
  }

  if (error || !currentReading) {
    return (
      <div className="max-w-3xl mx-auto">
        <Card>
          <CardBody>
            <div className="flex flex-col items-center py-8">
              <span className="text-red-500 text-4xl mb-4">!</span>
              <h2 className="text-xl font-medium mb-2">Error</h2>
              <p className="text-ink/70 dark:text-parchment/60 text-center mb-6">
                {error || 'There was a problem with your tarot reading.'}
              </p>
              <div className="space-y-4">
                <Button
                  variant="primary"
                  onClick={() => navigate('/new-reading')}
                >
                  Start New Reading
                </Button>
                {saveAttempts > 0 && (
                  <p className="text-sm text-ink/50 dark:text-parchment/60">
                    Save attempts: {saveAttempts}/{maxSaveAttempts}
                  </p>
                )}
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  const interpretation = currentReading.aiInterpretation || 'Your detailed interpretation will appear here, with insights about each card in your spread and how they relate to your question.';
  const advice = currentReading.advice || 'Practical advice based on your reading will be shown here.';
  const themes = currentReading.themes || [
    { name: 'Transformation', color: 'bg-vellum text-sun dark:bg-shadow dark:text-ember' },
    { name: 'Balance', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' },
    { name: 'Intuition', color: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300' }
  ];

  return (
    <AmbientBackground mood="moon" backgroundImage="/bg-interpretation.png">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="max-w-3xl mx-auto py-8 px-4"
      >
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="mb-8"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="font-display text-3xl md:text-4xl font-semibold text-moon dark:text-dusk">
                {currentReading.readingTitle || 'Tarot Interpretation'}
              </h1>
              <p className="text-sm text-ink/60 dark:text-parchment/50 mt-1">
                For {currentReading.subject} &middot; {new Date().toLocaleDateString()}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {themes.map((theme, index) => (
                <span
                  key={index}
                  className="bg-moon/10 text-moon dark:bg-dusk/30 dark:text-parchment/70 px-3 py-1 rounded-full text-xs font-medium tracking-wide"
                >
                  {theme.name}
                </span>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Content */}
        <div className="space-y-8">
          {/* Enhanced Interpretation */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <h2 className="font-display text-xl font-semibold text-moon dark:text-dusk mb-3">Enhanced Interpretation</h2>
            <div className="bg-parchment/50 dark:bg-shadow/40 p-6 rounded-lg border border-moon/20 dark:border-dusk/30">
              <div className="prose dark:prose-invert max-w-none">
                {interpretation.split('\n\n').map((paragraph, index) => (
                  <p key={index} className="mb-4 last:mb-0 text-base leading-relaxed text-ink dark:text-parchment/80">{paragraph}</p>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Advice */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.35 }}
          >
            <h2 className="font-display text-xl font-semibold text-moon dark:text-dusk mb-3">Advice</h2>
            <div className="bg-parchment/50 dark:bg-shadow/40 p-6 rounded-lg border border-moon/20 dark:border-dusk/30">
              <div className="prose dark:prose-invert max-w-none">
                {advice.split('\n\n').map((paragraph, index) => (
                  <p key={index} className="mb-4 last:mb-0 text-base leading-relaxed text-ink dark:text-parchment/80">{paragraph}</p>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Save / Auth Section */}
          {user ? (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.5 }}
              className="space-y-6"
            >
              <div>
                <label className="font-display italic text-lg text-ink dark:text-parchment/70 block mb-3">
                  Reflect on this reading...
                </label>
                <div className="relative">
                  <textarea
                    rows={4}
                    className="w-full px-3 py-2 pr-12 border border-mist dark:border-dusk rounded-md focus:outline-none focus:ring-2 focus:ring-moon focus:border-moon bg-vellum dark:bg-shadow transition-colors duration-200"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add your personal reflections on this reading..."
                  />
                  <div className="absolute right-2 top-2">
                    <VoiceInput
                      onTranscript={(text) => setNotes(prev => prev + (prev ? ' ' : '') + text)}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  variant="primary"
                  onClick={handleSave}
                  isLoading={isSaving}
                  disabled={isSaving}
                >
                  Save to Journal
                </Button>
              </div>

              {saveAttempts > 0 && (
                <p className="text-sm text-center text-ink/50 dark:text-parchment/60">
                  Save attempts: {saveAttempts}/{maxSaveAttempts}
                </p>
              )}
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.5 }}
              className="bg-parchment/50 dark:bg-shadow/40 p-6 rounded-lg border border-moon/20 dark:border-dusk/30 text-center"
            >
              <span className="text-4xl text-moon dark:text-dusk block mx-auto mb-4">✧</span>
              <h3 className="text-xl font-display font-bold text-moon dark:text-dusk mb-3">
                Want to save this reading?
              </h3>
              <p className="text-ink dark:text-parchment/60 mb-6">
                Create an account to save your readings, track your journey, and build your personal tarot journal.
              </p>
              <div className="flex justify-center gap-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    storePendingReading();
                    navigate('/login');
                  }}
                >
                  Log In
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    storePendingReading();
                    navigate('/signup');
                  }}
                >
                  Sign Up
                </Button>
              </div>
            </motion.div>
          )}

          {/* Context */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.6 }}
          >
            <h2 className="font-display text-xl font-semibold text-moon dark:text-dusk mb-3">Context About the Reading</h2>
            <div className="bg-parchment/50 dark:bg-shadow/40 p-6 rounded-lg border border-moon/20 dark:border-dusk/30">
              <p className="text-base leading-relaxed text-ink dark:text-parchment/80">{currentReading.context}</p>
            </div>
          </motion.div>

          {/* Cards */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.7 }}
          >
            <h2 className="font-display text-xl font-semibold text-moon dark:text-dusk mb-3">Cards</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {currentReading.cards?.map((card, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: 0.7 + index * 0.08 }}
                  className="bg-parchment/50 dark:bg-shadow/40 border border-moon/20 dark:border-dusk/30 rounded-lg p-3"
                >
                  <p className="font-display italic text-xs text-moon/70 dark:text-dusk/70">
                    {card.position}
                  </p>
                  <p className="font-medium text-ink dark:text-parchment">
                    {card.name} {card.isReversed && '(Reversed)'}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* User Interpretation */}
          {currentReading.userInterpretation && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.8 }}
            >
              <h2 className="font-display text-xl font-semibold text-moon dark:text-dusk mb-3">Your Interpretation</h2>
              <div className="bg-parchment/50 dark:bg-shadow/40 p-6 rounded-lg border border-moon/20 dark:border-dusk/30">
                <p className="text-base leading-relaxed text-ink dark:text-parchment/80">{currentReading.userInterpretation}</p>
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>
    </AmbientBackground>
  );
};

export default InterpretationView;
